import { useState } from 'react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { athletesApi } from '../../api/athletes';
import { groupsApi } from '../../api/groups';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { BackButton } from '../../components/ui/BackButton';
import type { Group } from '../../types/api';

type ImportStage = 'form' | 'progress' | 'result';

interface ImportResult {
  total_parsed: number;
  created: number;
  updated: number;
  failed: number;
  errors: string[];
}

export function ImportAthletesScreen() {
  const { getCurrentScreen, popScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const queryClient = useQueryClient();

  const assistantId = (currentScreen.params?.assistantId as string) || currentAssistantId || '';

  const [stage, setStage] = useState<ImportStage>('form');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ['groups', assistantId],
    queryFn: () => groupsApi.getByAssistantId(assistantId),
    enabled: !!assistantId,
  });

  const handleBackToList = () => {
    queryClient.invalidateQueries({ queryKey: ['athletes', assistantId] });
    popScreen();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleImport = async () => {
    if (!assistantId || !selectedFile) return;

    setStage('progress');

    try {
      const result = await athletesApi.importFromFile(
        assistantId,
        selectedFile,
        selectedGroupId || undefined
      );

      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response format from server');
      }

      setImportResult(result);
      setStage('result');
    } catch (error: any) {
      const errorMessage = error?.error || error?.message || error?.toString() || 'Не удалось импортировать файл. Попробуйте еще раз.';
      setImportResult({
        total_parsed: 0,
        created: 0,
        updated: 0,
        failed: 1,
        errors: [errorMessage],
      });
      setStage('result');
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  };

  if (stage === 'progress') {
    return (
      <div className="min-h-screen p-4 flex flex-col items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="w-full max-w-md space-y-6">
          <h2 className="text-2xl font-bold text-center" style={{ color: 'var(--text-primary)' }}>
            Импорт спортсменов
          </h2>
          <div className="space-y-4">
            <p className="text-center" style={{ color: 'var(--text-hint)' }}>
              Обработка файла с помощью AI...
            </p>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <div className="h-full animate-progress-indeterminate" style={{ backgroundColor: 'var(--accent)' }} />
            </div>
            <p className="text-center text-sm" style={{ color: 'var(--text-hint)' }}>
              Это может занять до 2 минут
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'result' && importResult) {
    const hasErrors = importResult.failed > 0 || importResult.errors.length > 0;
    const hasSuccess = importResult.created > 0 || importResult.updated > 0;

    return (
      <div className="min-h-screen p-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="max-w-md mx-auto space-y-6">
          <h2 className="text-2xl font-bold text-center" style={{ color: 'var(--text-primary)' }}>
            Результат импорта
          </h2>

          <Card>
            <div className="space-y-4">
              {hasSuccess && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span style={{ color: 'var(--text-primary)' }}>Всего обработано:</span>
                    <span className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{importResult.total_parsed}</span>
                  </div>
                  {importResult.created > 0 && (
                    <div className="flex justify-between items-center">
                      <span style={{ color: '#22c55e' }}>Создано:</span>
                      <span className="text-xl font-bold" style={{ color: '#22c55e' }}>{importResult.created}</span>
                    </div>
                  )}
                  {importResult.updated > 0 && (
                    <div className="flex justify-between items-center">
                      <span style={{ color: 'var(--accent)' }}>Обновлено:</span>
                      <span className="text-xl font-bold" style={{ color: 'var(--accent)' }}>{importResult.updated}</span>
                    </div>
                  )}
                </div>
              )}

              {hasErrors && (
                <div className="space-y-2 pt-4" style={{ borderTop: '1px solid var(--separator)' }}>
                  <div className="flex justify-between items-center">
                    <span style={{ color: 'var(--danger)' }}>Ошибок:</span>
                    <span className="text-xl font-bold" style={{ color: 'var(--danger)' }}>{importResult.failed}</span>
                  </div>
                  {importResult.errors.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm" style={{ color: 'var(--text-hint)' }}>
                        Показать детали ошибок
                      </summary>
                      <ul className="mt-2 space-y-1 text-sm" style={{ color: 'var(--danger)' }}>
                        {importResult.errors.map((error, idx) => (
                          <li key={idx} className="pl-4">{error}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
          </Card>

          <Button onClick={handleBackToList} fullWidth>
            Назад к списку спортсменов
          </Button>
        </div>
      </div>
    );
  }

  // Form stage
  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-md mx-auto space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <BackButton />
        </div>

        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Импорт спортсменов
        </h2>

        <Card>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Выбрать файл
              </label>
              <input
                type="file"
                accept=".txt,.csv,.xlsx,.xls,.json"
                onChange={handleFileChange}
                style={inputStyle}
              />
              {selectedFile && (
                <p className="mt-2 text-sm" style={{ color: 'var(--text-hint)' }}>
                  Выбран: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>

            <p className="text-sm" style={{ color: 'var(--text-hint)' }}>
              Поддерживаются: текстовые файлы, CSV, Excel
            </p>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Группа (опционально)
              </label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
              >
                <option value="">Без группы</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        <Button onClick={handleImport} disabled={!selectedFile} fullWidth>
          Импортировать
        </Button>

        <p className="text-xs text-center" style={{ color: 'var(--text-hint)' }}>
          AI автоматически распознает формат файла и извлечет данные о спортсменах.
          Дубликаты не будут импортированы, существующие данные будут обновлены.
        </p>
      </div>
    </div>
  );
}
